<template>
    <component
        :is="disabled ? 'div' : 'form'"
        class="non-human-identities-form"
        :action="disabled ? undefined : formAction"
        method="POST"
        accept-charset="UTF-8"
        @submit="onSubmit"
    >
        <template v-if="!disabled">
            <input type="hidden" name="_token" :value="csrfToken">
            <input v-if="formMethod === 'PUT'" type="hidden" name="_method" value="PUT">
        </template>

        <div class="panel panel-default">
            <div class="panel-heading"><strong>{{ labels.panel }}</strong></div>
            <div class="panel-body">
                <div class="row">
                    <div class="col-md-4">
                        <tenant-account-select
                            v-model="nhi.account_id"
                            :can-switch-account="canSwitchAccount"
                            :accounts-url="options.accountsUrl"
                            :label="labels.account + '*'"
                            :disabled="disabled || initialState.isEdit"
                            :error-message="fieldError('account_id')"
                        ></tenant-account-select>
                    </div>
                    <div class="col-md-4 form-group" :class="{ 'has-error': fieldError('slug') }">
                        <label class="control-label">{{ labels.slug }}*</label>
                        <input
                            v-model="nhi.slug"
                            class="form-control"
                            type="text"
                            name="slug"
                            :disabled="disabled || initialState.isEdit"
                            :readonly="disabled || initialState.isEdit"
                        >
                        <p v-if="fieldError('slug')" class="help-block">{{ fieldError('slug') }}</p>
                    </div>
                    <div class="col-md-4 form-group" :class="{ 'has-error': fieldError('display_name') }">
                        <label class="control-label">{{ labels.displayName }}*</label>
                        <input
                            v-model="nhi.display_name"
                            class="form-control"
                            type="text"
                            name="display_name"
                            :disabled="disabled"
                            :readonly="disabled"
                        >
                        <p v-if="fieldError('display_name')" class="help-block">{{ fieldError('display_name') }}</p>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('type') }">
                        <label class="control-label">{{ labels.type }}*</label>
                        <select v-model="nhi.type" class="form-control" name="type" :disabled="disabled">
                            <option v-for="(label, value) in options.types" :key="value" :value="value">{{ label }}</option>
                        </select>
                    </div>
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('status') }">
                        <label class="control-label">{{ labels.status }}*</label>
                        <select v-model="nhi.status" class="form-control" name="status" :disabled="disabled">
                            <option v-for="(label, value) in options.statuses" :key="value" :value="value">{{ label }}</option>
                        </select>
                    </div>
                    <div class="col-md-3">
                        <tenant-user-select
                            v-model="nhi.owner_user_id"
                            :account-id="nhi.account_id"
                            :users-url="options.usersUrl"
                            :prefetched-users="tenantUsers"
                            name="owner_user_id"
                            :label="labels.owner"
                            :empty-label="labels.ownerNone"
                            :allow-empty="true"
                            :disabled="disabled"
                            :error-message="fieldError('owner_user_id')"
                            :fallback-label="nhi.owner_label || ''"
                            input-id="nhi-owner-user-select"
                        ></tenant-user-select>
                    </div>
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('nhi_role_id') }">
                        <label class="control-label">{{ labels.nhiRole }}</label>
                        <select
                            v-model="nhi.nhi_role_id"
                            class="form-control"
                            name="nhi_role_id"
                            :disabled="disabled"
                        >
                            <option :value="null">{{ labels.nhiRoleNone }}</option>
                            <option v-for="(label, value) in options.nhiRoles" :key="value" :value="Number(value)">{{ label }}</option>
                        </select>
                        <p v-if="fieldError('nhi_role_id')" class="help-block">{{ fieldError('nhi_role_id') }}</p>
                    </div>
                </div>
                <div v-if="nhi.agent_slug" class="row">
                    <div class="col-md-12">
                        <p class="help-block">
                            {{ labels.linkedAgent }}:
                            <strong>{{ nhi.agent_slug }}</strong>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!hideActions" class="clearfix form-actions">
            <div class="pull-left">
                <button v-if="!disabled" type="submit" class="btn btn-success">{{ labels.save }}</button>
            </div>
            <div class="pull-right">
                <a :href="backUrl" class="btn btn-primary">{{ labels.back }}</a>
            </div>
        </div>
    </component>
</template>

<script>
import TenantAccountSelect from '../agent-platform/TenantAccountSelect.vue';
import TenantUserSelect from '../agent-platform/TenantUserSelect.vue';

export default {
    components: {
        TenantAccountSelect,
        TenantUserSelect,
    },
    props: {
        formAction: {
            type: String,
            default: '',
        },
        formMethod: {
            type: String,
            default: 'POST',
        },
        csrfToken: {
            type: String,
            default: '',
        },
        backUrl: {
            type: String,
            default: '',
        },
        initialState: {
            type: Object,
            required: true,
        },
        options: {
            type: Object,
            required: true,
        },
        validationErrors: {
            type: Object,
            default: () => ({}),
        },
        canSwitchAccount: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hideActions: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            nhi: { ...this.initialState.nhi },
            tenantUsers: [...(this.options.tenantUsers || [])],
        };
    },
    computed: {
        labels() {
            return this.options.labels || {};
        },
    },
    watch: {
        'nhi.account_id': {
            async handler(accountId) {
                if (accountId > 0) {
                    await this.refreshTenantUsers(accountId);
                }
            },
        },
    },
    methods: {
        fieldError(field) {
            const messages = this.validationErrors[field];

            return Array.isArray(messages) ? messages[0] : '';
        },
        async refreshTenantUsers(accountId) {
            if (! this.options.usersUrl || accountId <= 0) {
                return;
            }

            const url = `${this.options.usersUrl}?account_id=${encodeURIComponent(accountId)}`;
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (! response.ok) {
                return;
            }

            const payload = await response.json();
            this.tenantUsers = payload.users || [];
        },
        onSubmit(event) {
            if (this.disabled) {
                event.preventDefault();
            }
        },
    },
};
</script>
